import React, { memo, useEffect, useRef } from 'react';

interface MenuItem {
    label: string;
    action: () => void;
}

interface ContextMenuProps {
    x: number;
    y: number;
    menuItems: MenuItem[];
    onClose: () => void;
}

/**
 * Context menu component
 * @param {Object} props - Component props
 * @param {number} props.x - X position
 * @param {number} props.y - Y position
 * @param {Array} props.menuItems - Menu items to display
 * @param {Function} props.onClose - Function to close the menu
 * @returns {JSX.Element} - Rendered component
 */
const ContextMenu = memo(({ x, y, menuItems, onClose }: ContextMenuProps) => {
    const menuRef = useRef<HTMLDivElement>(null);

    // Handle all events to prevent propagation and default behavior
    const stopEvent = (e: React.SyntheticEvent): void => {
        e.preventDefault();
        e.stopPropagation();
    };

    // Position the menu within viewport bounds
    useEffect(() => {
        if (menuRef.current) {
            const rect = menuRef.current.getBoundingClientRect();
            const viewportWidth = window.innerWidth;
            const viewportHeight = window.innerHeight;

            let adjustedX = x;
            let adjustedY = y;

            if (x + rect.width > viewportWidth) {
                adjustedX = viewportWidth - rect.width - 5;
            }

            if (y + rect.height > viewportHeight) {
                adjustedY = viewportHeight - rect.height - 5;
            }

            menuRef.current.style.left = `${adjustedX}px`;
            menuRef.current.style.top = `${adjustedY}px`;
        }
    }, [x, y]);

    return (
        <div
            id="context-menu"
            ref={menuRef}
            className="fixed z-[100000] rounded-sm border bg-white shadow-lg"
            style={{ left: `${x}px`, top: `${y}px` }}
            onClick={stopEvent}
            onMouseDown={stopEvent}
            onMouseUp={stopEvent}
            onContextMenu={stopEvent}
        >
            <ul className="py-1">
                {menuItems.map((item, index) => (
                    <li key={index}>
                        <button
                            className="w-full px-4 py-2 text-left hover:bg-gray-100"
                            onClick={(e: React.MouseEvent) => {
                                stopEvent(e);
                                item.action();
                                onClose();
                            }}
                            onMouseDown={stopEvent}
                        >
                            {item.label}
                        </button>
                    </li>
                ))}
            </ul>
        </div>
    );
});

export default ContextMenu;
